/**
 * Pro Tools-style SVG waveform timeline.
 *
 * Layout (matches PT edit window):
 *   - White track background (#ffffff) with light grid lines
 *   - Pre-roll left band shaded grey + 3-pop tick marks
 *   - Cue IN/OUT vertical green markers with PT-style labels
 *   - Reference waveform: dark blue (#2563eb) filled silhouette
 *   - Take waveform overlay: bolder blue with subtle inner stroke
 *   - Red 1px playhead line + small triangular cap (PT chase head)
 *   - Time axis with mono numerals along the bottom
 */
import { useMemo } from 'react'
import { useRecordingStore } from './state'

export interface WaveformTimelineProps {
  /** Cue duration in seconds — drives waveform width inside the cue band. */
  cueDurationSec: number
  /** Reference voice peaks, normalised to [-1, 1]. */
  refPeaks: number[] | null
  /** Recorded take peaks, same encoding as refPeaks. */
  takePeaks: number[] | null
  /** SMPTE-formatted in/out for display. */
  cueInDisplay: string
  cueOutDisplay: string
  className?: string
}

const SVG_W = 800
const SVG_H = 220
const PRE_ROLL_X = 500
const POST_ROLL_X = 720
const CENTER_Y = 110
const AMP_SCALE = 78

export function WaveformTimeline({
  cueDurationSec,
  refPeaks,
  takePeaks,
  cueInDisplay,
  cueOutDisplay,
  className,
}: WaveformTimelineProps) {
  const playheadSec = useRecordingStore((s) => s.playheadSec)
  const phase = useRecordingStore((s) => s.phase)

  const playheadX = useMemo(() => {
    if (playheadSec <= 0) return 0
    return Math.min(SVG_W, playheadSec * 100)
  }, [playheadSec])

  const refPath = useMemo(
    () => peaksToPath(refPeaks, PRE_ROLL_X, POST_ROLL_X, cueDurationSec, false),
    [refPeaks, cueDurationSec],
  )
  const refPathBot = useMemo(
    () => peaksToPath(refPeaks, PRE_ROLL_X, POST_ROLL_X, cueDurationSec, true),
    [refPeaks, cueDurationSec],
  )
  const takePath = useMemo(
    () => peaksToPath(takePeaks, PRE_ROLL_X, POST_ROLL_X, cueDurationSec, false),
    [takePeaks, cueDurationSec],
  )
  const takePathBot = useMemo(
    () => peaksToPath(takePeaks, PRE_ROLL_X, POST_ROLL_X, cueDurationSec, true),
    [takePeaks, cueDurationSec],
  )
  const playheadVisible = phase === 'three_pop' || phase === 'recording'

  return (
    <svg
      role="img"
      aria-label={`Waveform timeline, cue from ${cueInDisplay} to ${cueOutDisplay}`}
      viewBox={`0 0 ${SVG_W} ${SVG_H}`}
      className={className ?? 'w-full h-full'}
      preserveAspectRatio="none"
      style={{ display: 'block' }}
    >
      <defs>
        <linearGradient id="pt-wave-take" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#1e40af" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#2563eb" stopOpacity="0.7" />
        </linearGradient>
      </defs>

      {/* White track background. */}
      <rect x="0" y="20" width={SVG_W} height={SVG_H - 40} fill="#ffffff" />
      {/* Track outer frame — 1px black for the PT edit-window feel. */}
      <rect
        x="0.5"
        y="20.5"
        width={SVG_W - 1}
        height={SVG_H - 41}
        fill="none"
        stroke="#737378"
        strokeWidth="1"
      />

      {/* Light center hairline. */}
      <line x1="0" y1={CENTER_Y} x2={SVG_W} y2={CENTER_Y} stroke="#d8d8dc" strokeWidth="0.6" />

      {/* Pre-roll shaded area (light grey panel). */}
      <rect x="0" y="20" width={PRE_ROLL_X} height={SVG_H - 40} fill="#eeeeef" />
      <text
        x={PRE_ROLL_X / 2}
        y={CENTER_Y + 4}
        textAnchor="middle"
        fill="#5a5a5e"
        fontSize="11"
        letterSpacing="2"
        fontFamily="'SF Mono', Monaco, monospace"
      >
        PRE-ROLL · 5s
      </text>

      {/* 3-pop beep markers — amber tick lines. */}
      {[200, 300, 400].map((bx) => (
        <g key={bx}>
          <line
            x1={bx}
            y1={CENTER_Y - 60}
            x2={bx}
            y2={CENTER_Y + 60}
            stroke="#ffaa1d"
            strokeWidth="1"
            strokeDasharray="2,4"
            opacity="0.85"
          />
          <circle cx={bx} cy={CENTER_Y - 70} r="2.5" fill="#ffaa1d" />
        </g>
      ))}

      {/* Cue IN green vertical marker. */}
      <line
        x1={PRE_ROLL_X}
        y1="20"
        x2={PRE_ROLL_X}
        y2={SVG_H - 20}
        stroke="#16a34a"
        strokeWidth="2"
      />
      <rect x={PRE_ROLL_X - 22} y="6" width="44" height="14" rx="2" fill="#16a34a" />
      <text
        x={PRE_ROLL_X}
        y="16"
        textAnchor="middle"
        fill="#ffffff"
        fontSize="9"
        fontWeight="700"
        letterSpacing="1"
        fontFamily="'SF Mono', Monaco, monospace"
      >
        CUE IN
      </text>

      {/* Reference waveform — dark blue silhouette (PT track look). */}
      {refPath && (
        <path
          d={refPath}
          fill="none"
          stroke="#2563eb"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {refPathBot && (
        <path
          d={refPathBot}
          fill="none"
          stroke="#2563eb"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {/* Actor's take waveform — slightly thicker, darker blue overlay. */}
      {takePath && takePathBot && (
        <g>
          <path
            d={takePath}
            fill="none"
            stroke="url(#pt-wave-take)"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d={takePathBot}
            fill="none"
            stroke="url(#pt-wave-take)"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </g>
      )}

      {/* Cue OUT green vertical marker. */}
      <line
        x1={POST_ROLL_X}
        y1="20"
        x2={POST_ROLL_X}
        y2={SVG_H - 20}
        stroke="#16a34a"
        strokeWidth="2"
      />
      <rect x={POST_ROLL_X - 24} y="6" width="48" height="14" rx="2" fill="#16a34a" />
      <text
        x={POST_ROLL_X}
        y="16"
        textAnchor="middle"
        fill="#ffffff"
        fontSize="9"
        fontWeight="700"
        letterSpacing="1"
        fontFamily="'SF Mono', Monaco, monospace"
      >
        CUE OUT
      </text>

      {/* Post-roll shaded area. */}
      <rect x={POST_ROLL_X} y="20" width={SVG_W - POST_ROLL_X} height={SVG_H - 40} fill="#eeeeef" />
      <text
        x={(POST_ROLL_X + SVG_W) / 2}
        y={CENTER_Y + 4}
        textAnchor="middle"
        fill="#5a5a5e"
        fontSize="10"
        letterSpacing="2"
        fontFamily="'SF Mono', Monaco, monospace"
      >
        POST · 3s
      </text>

      {/* Playhead — red 1px line + downward triangle cap (PT chase head). */}
      <g style={{ opacity: playheadVisible ? 1 : 0 }}>
        <line
          x1={playheadX}
          y1="14"
          x2={playheadX}
          y2={SVG_H - 14}
          stroke="#ef4444"
          strokeWidth="1"
        />
        <polygon
          points={`${playheadX - 6},14 ${playheadX + 6},14 ${playheadX},22`}
          fill="#ef4444"
        />
      </g>

      {/* Time axis — black hairline with mono labels. */}
      <line x1="0" y1={SVG_H - 18} x2={SVG_W} y2={SVG_H - 18} stroke="#737378" strokeWidth="0.75" />
      {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((s) => {
        const x = s * 100
        const isCueIn = s === 5
        return (
          <g key={s}>
            <line
              x1={x}
              y1={SVG_H - 18}
              x2={x}
              y2={SVG_H - 14}
              stroke={isCueIn ? '#16a34a' : '#737378'}
              strokeWidth={isCueIn ? '1.5' : '0.75'}
            />
            <text
              x={x === 780 ? 780 : x + 4}
              y={SVG_H - 4}
              fill={isCueIn ? '#16a34a' : '#5a5a5e'}
              fontSize="9"
              fontFamily="'SF Mono', Monaco, monospace"
              fontWeight={isCueIn ? '700' : '400'}
              letterSpacing="0.5"
            >
              {s}s
            </text>
          </g>
        )
      })}
    </svg>
  )
}

/**
 * Build an SVG path for a peak array between [xStart, xEnd], normalised so
 * peak amplitude 1 maps to ±AMP_SCALE px from the centerline.
 */
function peaksToPath(
  peaks: number[] | null,
  xStart: number,
  xEnd: number,
  durationSec: number,
  mirror: boolean,
): string | null {
  if (!peaks || peaks.length === 0 || durationSec <= 0) return null
  const width = xEnd - xStart
  const cy = CENTER_Y
  const points: string[] = []
  for (let i = 0; i < peaks.length; i++) {
    const x = xStart + (i / Math.max(1, peaks.length - 1)) * width
    const raw = peaks[i] ?? 0
    const amp = Math.max(-1, Math.min(1, raw)) * AMP_SCALE
    const y = mirror ? cy + Math.abs(amp) : cy - Math.abs(amp)
    points.push(`${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`)
  }
  return points.join(' ')
}
